import { useEffect, useState, useRef } from "react";
import { type PurchasePreview } from "../../components/PurchaseConfirmation";
import { useRoute, useLocation, useSearch } from "wouter";
import { Boxes, Wrench, Building2 } from "lucide-react";
import { api, type ArrangementCheckout, type PurchaseHandover } from "../../lib/api";
import { itemTypeLabel, itemPostingLabel, BUSINESS_OPERATING_STATUSES } from "../../data/itemTypes";
import { useAuth } from "../../lib/auth";
import { useInPageActionVisibility } from "../../lib/useInPageActionVisibility";
import { messagesHref } from "../../lib/actions";
import { useI18n } from "../../lib/i18n";
import { accountVerificationFrom } from "../../lib/accountVerification";
import { detailText, dateInputValue, rentalDaysBetween } from "./presentation";
import { preparePurchaseReview, purchaseCheckoutInput } from "../../lib/purchaseReview";
import { ApiError } from "../../lib/api-client/transport";

type PublicSellerProfile = {
  name: string | null;
  identityVerified: boolean;
  verifiedProfessions: string[];
  city: string | null;
  province: string | null;
  memberSince: string | null;
  activeItemCount: number | null;
};

export function useItemDetail() {
  const { lang, t } = useI18n();
  const [, params] = useRoute("/item/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const search = useSearch();
  const arrangementId = new URLSearchParams(search).get("arrangement") || "";

  const [item, setItem] = useState<any>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound">("loading");
  const [qty, setQty] = useState("1");
  const [rentalStart, setRentalStart] = useState(() => dateInputValue(1));
  const [rentalEnd, setRentalEnd] = useState(() => dateInputValue(4));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [placed, setPlaced] = useState<any>(null);
  const [purchasePreview, setPurchasePreview] = useState<PurchasePreview | null>(null);
  const paymentPending = useRef(false);
  const reviewEpoch = useRef(0);
  const [requestOpen, setRequestOpen] = useState(false);
  const [arrangement, setArrangement] = useState<ArrangementCheckout | null>(null);
  const [arrangementError, setArrangementError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<{ average: number; count: number; breakdown: Record<string, number>; reviews: any[] } | null>(null);
  const [sellerProfile, setSellerProfile] = useState<PublicSellerProfile | null>(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  const orderRef = useRef<HTMLDivElement>(null);
  const { actionRef, isVisible } = useInPageActionVisibility<HTMLFormElement>(isMobileViewport ? null : id);

  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(viewport.matches);
    updateViewport();
    viewport.addEventListener("change", updateViewport);
    return () => viewport.removeEventListener("change", updateViewport);
  }, []);

  useEffect(() => {
    let alive = true;
    ++reviewEpoch.current;
    setState("loading");
    setPurchasePreview(null);
    setReviews(null);
    setPlaced(null);
    setErr(null);
    api.getItem(id)
      .then((it) => {
        if (!alive) return;
        if (it) {
          setItem(it); setState("ready");
          if (it.postingIdentity?.kind !== "business" && it.ownerUserId) api.userReviews(it.ownerUserId, "marketplace").then((rv) => { if (alive) setReviews(rv); }).catch(() => {});
        } else setState("notfound");
      })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [id]);


  useEffect(() => {
    const sellerId = Number(item?.ownerUserId);
    if (item?.postingIdentity?.kind === "business" || !Number.isSafeInteger(sellerId) || sellerId <= 0) { setSellerProfile(null); return; }
    let alive = true;
    setSellerProfile(null);
    api.getProfile(sellerId).then((profile) => {
      if (!alive) return;
      setSellerProfile({
        name: typeof profile?.user?.name === "string" ? profile.user.name : null,
        ...accountVerificationFrom(profile?.user),
        city: typeof profile?.user?.city === "string" ? profile.user.city : null,
        province: typeof profile?.user?.province === "string" ? profile.user.province : null,
        memberSince: typeof profile?.user?.memberSince === "string" ? profile.user.memberSince : null,
        activeItemCount: Number.isInteger(profile?.counts?.items) ? profile.counts.items : null,
      });
    }).catch(() => { if (alive) setSellerProfile(null); });
    return () => { alive = false; };
  }, [item?.ownerUserId, item?.postingIdentity?.kind]);

  useEffect(() => {
    let alive = true;
    setArrangement(null); setArrangementError(null); setRequestOpen(false);
    if (arrangementId && user) api.getItemArrangementQuote(arrangementId).then(({ quote }) => {
      if (!alive) return;
      if (quote.itemId !== id || quote.buyerUserId !== user.id) {
        setArrangementError("This arrangement is not available for checkout. Review it in Messages."); return;
      }
      // [items.stock-and-replay](../../../../docs/product/items.md#items-stock-and-replay): stale Buy now links for consumed terms open the
      // original purchase. The server also replays its order without another debit.
      if (quote.status === "purchased" && quote.orderId) {
        navigate(`/contracts?order=${encodeURIComponent(quote.orderId)}`); return;
      }
      if (quote.status !== "accepted") { setArrangementError("This arrangement is not available for checkout. Review it in Messages."); return; }
      setArrangement(quote);
    }).catch((failure: unknown) => { if (alive) setArrangementError(failure instanceof Error ? failure.message : "Could not load the arrangement."); });
    return () => { alive = false; };
  }, [arrangementId, id, user, navigate]);
  if (state === "loading") return { status: "loading" as const, label: t("detail.item.loading") };
  if (state === "notfound" || !item) return { status: "notfound" as const, title: t("detail.item.notFound"), backLabel: t("detail.item.browse") };

  const isRent = item.mode === "rent";
  const isEquip = item.kind === "equipment";
  const isProperty = item.kind === "property";
  const isBusiness = item.kind === "business";
  const kindLabel = item.itemType ? itemPostingLabel(item.itemType, item.kind) : t(`detail.item.kind.${item.kind}`, itemTypeLabel(item.kind));
  const KindIcon = isProperty || isBusiness ? Building2 : isEquip ? Wrench : Boxes;
  const isOwner = !!user && user.id === item.ownerUserId;
  const active = item.status === "active" || (!!arrangement && item.status === "archived");
  const inStock = (item.stock ?? 0) > 0;
  const q = arrangement?.qty ?? Number(qty);
  const maxQuantity = Math.min(100000, item.stock ?? 0);
  const quantityValid = Number.isSafeInteger(q) && q >= 1 && q <= maxQuantity;
  const days = rentalDaysBetween(rentalStart, rentalEnd);
  const handover: PurchaseHandover | null = arrangement ? { method: arrangement.method, location: arrangement.location, schedule: arrangement.schedule, note: arrangement.note, fee: arrangement.fee, quoteId: arrangement.id }
    : !isProperty && !isBusiness && item.handoverOptions?.includes("pickup") && item.pickupLocation && item.pickupHours
      ? { method: "pickup", location: item.pickupLocation, schedule: item.pickupHours, note: "", fee: 0, quoteId: null } : null;
  const total = !quantityValid ? null : arrangement ? arrangement.unitPrice * q + arrangement.fee : isRent ? (days === null ? null : item.price * q * days) : item.price * q;
  const canOrder = active && inStock && !isOwner && !isBusiness && !isRent;
  // [items.checkout](../../../../docs/product/items.md#items-checkout): physical checkout is shown only with pickup defaults or accepted private terms.
  const showBuyNow = isProperty || !!handover;
  const canSubmit = canOrder && showBuyNow && quantityValid && (!arrangementId || !!arrangement) && (!isRent || days !== null);
  function openArrangement() {
    if (!user) { navigate("/login"); return; }
    if (canOrder && !isProperty && quantityValid) { setErr(null); setRequestOpen(true); }
  }

  async function reviewPurchase(e?: React.FormEvent) {
    e?.preventDefault();
    setErr(null);
    if (isRent) { setErr(t("detail.item.rentalsPaused", "Marketplace rentals are temporarily unavailable.")); return; }
    if (!user) { navigate("/login"); return; }
    if (!canSubmit || busy) return;
    if (q > item.stock) { setErr(t("detail.item.availableOnly").replace("{count}", String(item.stock))); return; }
    // [items.checkout](../../../../docs/product/items.md#items-checkout): Buy now reviews a snapshot; only modal confirmation submits payment.
    const epoch = reviewEpoch.current;
    setBusy(true);
    try {
      const preview = await preparePurchaseReview({ itemId: item.id, title: item.title, seller: item.ownerName || "Seller", qty: q, unitPrice: arrangement?.unitPrice ?? item.price, total: total ?? item.price * q, paymentMethod: "wallet", handover, milestones: arrangement?.milestones }, !!(arrangement ? arrangement.purchaseOfferId : item.purchaseOfferId));
      if (epoch === reviewEpoch.current) setPurchasePreview(preview);
    } catch (failure: unknown) { if (epoch === reviewEpoch.current) setErr(failure instanceof Error ? failure.message : "Could not load the purchase agreement. Retry."); }
    finally { if (epoch === reviewEpoch.current) setBusy(false); }
  }

  async function confirmPurchase() {
    if (!purchasePreview || paymentPending.current || !user || !canSubmit || purchasePreview.itemId !== id) return;
    if (purchasePreview.qty > item.stock) { setErr(t("detail.item.availableOnly").replace("{count}", String(item.stock))); return; }
    paymentPending.current = true;
    setErr(null);
    setBusy(true);
    try {
      const res = await api.placeOrder(purchaseCheckoutInput(purchasePreview));
      if (res?.order) {
        setPlaced(res.order);
        setPurchasePreview(null);
        navigate(`/contracts?order=${encodeURIComponent(res.order.id)}`);
      }
      else setErr(t("detail.item.orderFailed"));
    } catch (error: unknown) {
      if (purchasePreview.requestKey && (!(error instanceof ApiError) || error.status >= 500)) setPurchasePreview(current => current ? { ...current, uncertain: true } : current);
      setErr(error instanceof Error ? error.message : t("detail.item.orderFailed"));
    }
    finally { paymentPending.current = false; setBusy(false); }
  }

  const displayDetail = (value: unknown) => detailText(value, t("detail.item.notProvided"));
  const condition = item.condition === "used" ? t("detail.item.used") : item.condition === "new" ? t("detail.item.new") : displayDetail(item.condition);
  const messageHref = messagesHref(item.ownerUserId, { type: "item", id, name: item.title, ...(isBusiness ? { intent: "quote", subject: item.title } : {}) });
  const detailRows = [
    [t("detail.item.condition"), !isBusiness && !isProperty && item.condition ? condition : null],
    [t("detail.item.operatingStatus"), isBusiness && item.businessOperatingStatus ? t(`detail.item.operatingStatus.${item.businessOperatingStatus}`, BUSINESS_OPERATING_STATUSES.find((status) => status.value === item.businessOperatingStatus)?.label || item.businessOperatingStatus) : null],
    ...(!isBusiness && !isProperty ? [
      [t("detail.item.brand"), item.brand], [t("detail.item.model"), item.model],
      [t("detail.item.storage"), item.storage], [t("detail.item.color"), item.color],
    ] : []),
  ].filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].trim().length > 0);
  const openMessage = () => {
    if (!user) { navigate("/login"); return; }
    if (messageHref && !isOwner) navigate(messageHref);
  };
  return { status: "ready" as const, lang, t, navigate, user, id, arrangementId, item, qty, setQty, rentalStart, setRentalStart, rentalEnd, setRentalEnd, busy, err, setErr, placed, purchasePreview, setPurchasePreview, requestOpen, setRequestOpen, arrangement, arrangementError, reviews, sellerProfile, reviewsOpen, setReviewsOpen, isMobileViewport, orderRef, actionRef, isVisible, isRent, isEquip, isProperty, isBusiness, kindLabel, KindIcon, isOwner, active, inStock, q, maxQuantity, quantityValid, days, handover, total, canOrder, showBuyNow, canSubmit, openArrangement, reviewPurchase, confirmPurchase, messageHref, detailRows, openMessage };
}

export type ItemDetailModel = Extract<ReturnType<typeof useItemDetail>, { status: "ready" }>;
